<template>
  <ValidationObserver
    v-if="internalFinancement.internal_id"
    ref="financementObserver"
    v-slot="{ handleSubmit }"
  >
    <BookmarkCard :tag-type="tagType" :icon-name="iconName">
      <template #content>
        <div style="width: 300px">
          <div v-if="financementAdded">
            <SelectWithValidation
              v-model="internalFinancement.origine"
              :label="$t('projetPage.infoAdministratives.financement.origine')"
              :data="origines"
              :select-labels="originesLabels"
              :rules="hasAtLeastOneFieldValued ? 'required' : ''"
              :disabled="readOnly"
              :vid="'financement_origine_' + internalFinancement.internal_id"
              @manual-change="hasAtLeastOneFieldValued = true"
            />
            <InputWithValidation
              v-if="internalFinancement.origine === 'AUTRE'"
              v-model="internalFinancement.origineAutreTexte"
              :in-card="true"
              :vid="'financement_origine_autre_texte_' + internalFinancement.internal_id"
              :rules="hasAtLeastOneFieldValued ? 'required' : ''"
              type="list"
              placeholder=""
              :label="$t('projetPage.infoAdministratives.financement.origineAutre')"
              :disabled="readOnly"
              @manual-change="hasAtLeastOneFieldValued = true"
            />
            <div class="dates-container">
              <DatePickerWithValidation
                ref="dateDebut"
                v-model="internalFinancement.debut"
                :vid="'financement_debut_' + internalFinancement.internal_id"
                :rules="hasAtLeastOneFieldValued ? 'required' : ''"
                :label="$t('projetPage.infoAdministratives.financement.dateDebut')"
                :placeholder="$t('ParametersPage.selectionner-date')"
                :disabled="readOnly"
                @manual-change="hasAtLeastOneFieldValued = true"
              />
              <DatePickerWithValidation
                ref="dateFin"
                v-model="internalFinancement.fin"
                :vid="'financement_fin_' + internalFinancement.internal_id"
                :rules="`${
                  internalFinancement.debut
                    ? `dateIsAfter:${internalFinancement.debut.toString()}` + '|required'
                    : ''
                }`"
                :label="$t('projetPage.infoAdministratives.financement.dateFin')"
                :placeholder="$t('ParametersPage.selectionner-date')"
                :disabled="readOnly"
                @manual-change="hasAtLeastOneFieldValued = true"
              />
            </div>

            <InputWithValidation
              v-if="!isValid() && hasAtLeastOneFieldValued"
              :vid="'financement_period_' + internalFinancement.internal_id"
              rules="periodFinancementValid"
              class="invisible-input"
            />

            <InputWithValidation
              v-model="internalFinancement.montant"
              :vid="'financement_montant_' + internalFinancement.internal_id"
              :rules="hasAtLeastOneFieldValued ? 'required' : ''"
              type="number"
              placeholder=""
              :label="$t('projetPage.infoAdministratives.financement.montant')"
              :disabled="readOnly"
              @manual-change="hasAtLeastOneFieldValued = true"
            />
          </div>
          <div v-else>{{ $t("projetPage.infoAdministratives.financement.description") }}</div>
        </div>
      </template>
      <template #footer>
        <div class="financement-footer">
          <b-button
            v-if="!financementAdded"
            type="is-primary is-outlined"
            rounded
            @click="financementAdded = true"
          >
            {{ $t("create") }}
          </b-button>
          <b-button
            v-if="!readOnly && financementAdded"
            type="is-primary is-outlined"
            :disabled="!hasAtLeastOneFieldValued"
            rounded
            @click="handleSubmit(addFinancement)"
          >
            {{ $t("add") }}
          </b-button>
          <b-button
            v-if="canDelete"
            type="is-danger is-outlined"
            rounded
            icon-left="trash"
            @click="deleteFinancement()"
          >
            {{ $t("delete") }}
          </b-button>
        </div>
      </template>
    </BookmarkCard>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { FinancementService } from "@/services/rest/financement/FinancementService"
import BookmarkCard from "../generic_components/BookmarkCard.vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import SelectWithValidation from "@/views/inputs/SelectWithValidation.vue"
import DatePickerWithValidation from "@/views/inputs/DatePickerWithValidation.vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { TAG_TYPES_FOR_BOOKMARK_CARD } from "@/model/bean/EnumHelper"
import { FinancementDTO, OrigineFinancement, ProjetAnnuelDTO } from "@/model/bean/GeneratedDTOs"
import ConversionUtils from "@/services/ConversionUtils"
import { BButton } from "buefy"

export default defineComponent({
  name: "FinancementCard",
  components: {
    BookmarkCard,
    InputWithValidation,
    DatePickerWithValidation,
    SelectWithValidation,
    ValidationObserver,
    BButton,
  },
  props: {
    financement: {
      type: Object as PropType<FinancementDTO>,
      required: false,
      default: undefined,
    },
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    canDelete: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["add", "delete"],
  data() {
    return {
      financementAdded: false,
      hasAtLeastOneFieldValued: false,
      infoReportingService: InfoReportingService.INSTANCE,
      financementService: FinancementService.INSTANCE,
      tagType: TAG_TYPES_FOR_BOOKMARK_CARD.NOUVEAU_FINANCEMENT,
      readOnly: false,
      iconName: "plus",
      internalFinancement: new FinancementDTO(),
      origines: [
        "AGENCE_EAU",
        "APPEL_PROJET",
        "AUTRE",
        "COMMUNAUTE_COMMUNE",
        "COMMUNE",
        "DEPARTEMENT",
        "EUROPE",
        "FONDATION_PRIVEE",
        "OFB",
        "INTERVENTION_EDD",
        "REGION",
        "STRUCTURE_GEST_ENV",
      ] as OrigineFinancement[],
      originesLabels: [] as string[],
    }
  },
  computed: {},
  watch: {
    financement(): void {
      this.financementChanged()
    },
    projetAnnuel(): void {
      requestAnimationFrame(() => {
        ;(this.$refs.financementObserver as InstanceType<typeof ValidationObserver>).reset()
      })
    },
  },
  created(): void {
    this.originesLabels = this.origines.map((value) => {
      return this.$t(`projetPage.infoAdministratives.financement.origineLabels.${value}`).toString()
    })
  },
  mounted(): void {
    this.financementChanged()
    if (
      this.internalFinancement.origine !== undefined &&
      this.internalFinancement.origine !== null
    ) {
      this.readOnly = true
      this.tagType = TAG_TYPES_FOR_BOOKMARK_CARD.FINANCEMENT
      this.iconName = "euro-sign"
    }
  },
  methods: {
    addFinancement(): void {
      this.financementAdded = false
      this.$emit("add", this.internalFinancement)
      this.financementChanged()

      //this.$refs.financementObserver.reset()
    },

    deleteFinancement(): void {
      this.infoReportingService.dialog(
        this.$t("projetPage.infoAdministratives.financement.confirmation").toString(),
        this.$t("projetPage.infoAdministratives.financement.are-you-sure").toString(),
        this.$t("projetPage.infoAdministratives.financement.supprimer").toString(),
        () => {
          this.$emit("delete", this.financement)
        },
        "is-info",
      )
    },

    financementChanged(): void {
      if (this.financement) {
        this.financementAdded = true
        this.internalFinancement = ConversionUtils.convertFinancementDatesToLocal(this.financement)
        // @ts-ignore
        this.internalFinancement.internal_id =
          // @ts-ignore
          this.financement.id || this.internalFinancement.internal_id
      } else {
        this.internalFinancement = new FinancementDTO()
        // @ts-ignore
        this.internalFinancement.internal_id = "new-" + (this.projetAnnuel.financements.length + 1)
      }
      requestAnimationFrame(() => {
        ;(this.$refs.financementObserver as InstanceType<typeof ValidationObserver>).reset()
      })
    },

    isValid(): boolean {
      return this.financementService.isValid(
        this.projetAnnuel,
        this.internalFinancement.debut,
        this.internalFinancement.fin,
      )
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

$financement-card-width: 320px;
.financement-footer {
  margin: auto;
}

.BookmarkCard {
  &.financement,
  &.nouveau-financement {
    height: auto;
    margin-left: 10px;
    margin-right: 10px;

    .BookmarkCard-tag,
    .BookmarkCard-icon {
      background-color: $ofb-bleu-1;
    }

    .BookmarkCard-content {
      height: auto;

      .mandatory {
        display: none;
      }

      .SelectWithValidation-field {
        width: $financement-card-width - 20px;
      }

      .dates-container {
        display: flex;
        justify-content: space-between;
        width: $financement-card-width - 20px;

        .DatePickerWithValidation-field {
          width: ($financement-card-width - 30px) /2;

          .datepicker {
            .dropdown {
              .dropdown-trigger {
                .control {
                  .is-success {
                    padding-right: 2px;
                  }

                  .has-text-success {
                    display: none;
                  }
                }
              }
            }
          }
        }

        span {
          br {
            display: none;
          }
        }
      }
    }

    .BookmarkCard-card {
      height: auto;
      width: $financement-card-width;
    }

    .BookmarkCard-tag {
      width: 120px;
      text-align: center;
      height: 50px;
      text-transform: none;
      font-size: 15px;
    }

    .BookmarkCard-icon {
      width: 120px;
      color: $ofb-vert-1;
    }

    .BookmarkCard-footer {
      height: 50px;
      margin: auto;
    }

    .invisible-input {
      .label,
      .InputWithValidation-control-container {
        display: none;
      }
    }
  }
}
</style>
